import ExcelJS from 'exceljs';
import type { PersonnelDetail } from '@/types/personnelDetail';
import { buildFullUrl } from '@/contexts/AvatarContext';
import { STORAGE_KEYS } from '@shared/constants';
import { formatRole } from '@/utils/formatters';

/**
 * Helper function để detect extension từ URL hoặc content type
 */
function getImageExtension(url: string, contentType?: string): 'png' | 'jpeg' | 'gif' {
  if (contentType) {
    if (contentType.includes('jpeg') || contentType.includes('jpg')) return 'jpeg';
    if (contentType.includes('png')) return 'png';
    if (contentType.includes('gif')) return 'gif';
  }

  const urlLower = url.toLowerCase();
  if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) return 'jpeg';
  if (urlLower.includes('.png')) return 'png';
  if (urlLower.includes('.gif')) return 'gif';

  return 'png'; // Default
}

/**
 * Helper function để fetch image và convert sang buffer cho ExcelJS
 */
async function fetchImageAsBuffer(
  imageUrl: string | null | undefined
): Promise<{ buffer: ArrayBuffer; extension: 'png' | 'jpeg' | 'gif' } | null> {
  if (!imageUrl) return null;

  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const fullUrl = buildFullUrl(imageUrl);

    if (!fullUrl) return null;

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {},
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const contentType = response.headers.get('content-type') || undefined;
    const extension = getImageExtension(fullUrl, contentType);
    const arrayBuffer = await response.arrayBuffer();

    return { buffer: arrayBuffer, extension };
  } catch (error) {
    console.error('Error fetching image:', error);
    return null;
  }
}

/**
 * Interface cho thông tin thay đổi
 */
export interface ChangedField {
  field: string;
  newValue: string;
  oldValue: string;
}

/**
 * Xuất thông tin nhân sự ra file Excel với nhiều sheet
 * Mỗi sheet tương ứng với một mục trong menu
 */
export async function exportPersonnelToExcel(
  data: PersonnelDetail,
  fileName: string = 'Ho_So_Nhan_Su.xlsx',
  changedFields?: ChangedField[]
): Promise<void> {
  const workbook = new ExcelJS.Workbook();

  // Helper function để tạo style cho header
  const getHeaderStyle = () => ({
    fill: {
      type: 'pattern' as const,
      pattern: 'solid' as const,
      fgColor: { argb: 'FF1F4E78' },
    },
    font: {
      bold: true,
      color: { argb: 'FFFFFFFF' },
      size: 11,
    },
    alignment: {
      vertical: 'middle' as const,
      horizontal: 'center' as const,
      wrapText: true,
    },
    border: {
      top: { style: 'thin' as const, color: { argb: 'FF000000' } },
      left: { style: 'thin' as const, color: { argb: 'FF000000' } },
      bottom: { style: 'thin' as const, color: { argb: 'FF000000' } },
      right: { style: 'thin' as const, color: { argb: 'FF000000' } },
    },
  });

  // Helper function để tạo style cho cell dữ liệu
  const getDataCellStyle = (isEvenRow: boolean = false) => ({
    fill: {
      type: 'pattern' as const,
      pattern: 'solid' as const,
      fgColor: { argb: isEvenRow ? 'FFF2F2F2' : 'FFFFFFFF' },
    },
    font: {
      size: 10,
      color: { argb: 'FF000000' },
    },
    border: {
      top: { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
      left: { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
      bottom: { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
      right: { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
    },
    alignment: {
      vertical: 'middle' as const,
      horizontal: 'left' as const,
      wrapText: true,
    },
  });

  // Helper function để format giá trị
  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') {
      return 'Chưa cập nhật';
    }
    if (typeof value === 'boolean') {
      return value ? 'Có' : 'Không';
    }
    return String(value);
  };

  // Helper function để lấy changed field theo field name
  const getChangedField = (fieldName: string): ChangedField | undefined => {
    if (!changedFields || changedFields.length === 0) return undefined;
    return changedFields.find((cf) => cf.field === fieldName);
  };

  // 1. Sheet: Thông tin cá nhân
  const personalInfoSheet = workbook.addWorksheet('Thông tin cá nhân');

  const personalInfoHeaders = [
    'Mã nhân sự',
    'Họ và tên',
    'Ngày sinh',
    'Giới tính',
    'Dân tộc',
    'Tôn giáo',
    'Tình trạng hôn nhân',
    'Nghĩa vụ quân sự',
    'Nơi sinh',
    'Nguyên quán',
    'Quốc tịch',
    'Ngày vào làm',
    'Ngày ký HĐLĐ chính thức',
    'Loại lao động',
    'Người quản lý trực tiếp',
    'Trình độ học vấn cao nhất',
    'Trình độ phổ thông',
    'Chuyên ngành',
    'Số năm kinh nghiệm',
    'Mã số thuế cá nhân',
    'Mô tả bản thân',
  ];

  const fieldNameMap: Record<number, string[]> = {
    0: ['employeeCode'],
    1: ['fullName'],
    2: ['dateOfBirth'],
    3: ['gender'],
    4: ['ethnicity'],
    5: ['religion'],
    6: ['maritalStatus'],
    7: ['militaryService'],
    8: ['placeOfBirth'],
    9: ['hometown'],
    10: ['nationality'],
    11: ['startDate'],
    12: ['officialContractDate'],
    13: ['laborType'],
    14: ['directManager'],
    15: ['education.highestLevel', 'generalEducationLevel'],
    16: ['education.generalLevel'],
    17: ['education.major', 'specialization'],
    18: ['education.yearsOfExperience', 'yearsOfExperience'],
    19: ['taxCode', 'personalTaxCode'],
    20: ['description', 'selfDescription'],
  };

  const personalInfoValues: any[] = [
    formatValue(data.employeeCode),
    formatValue(data.fullName),
    formatValue(data.dateOfBirth),
    formatValue(data.gender),
    formatValue(data.ethnicity),
    formatValue(data.religion),
    formatValue((data as any).maritalStatus),
    formatValue((data as any).militaryService),
    formatValue(data.placeOfBirth),
    formatValue(data.hometown?.replace('Huyện ', '').replace('Tỉnh ', '').replace(', Việt Nam', '')),
    formatValue(data.nationality),
    formatValue(data.startDate),
    formatValue((data as any).officialContractDate),
    formatValue((data as any).laborType),
    formatValue((data as any).directManager),
    formatValue(data.education?.highestLevel),
    formatValue(data.education?.generalLevel),
    formatValue(data.education?.major),
    formatValue(data.education?.yearsOfExperience),
    formatValue(data.taxCode),
    formatValue(data.description),
  ];

  personalInfoSheet.addRow(personalInfoHeaders);
  const personalInfoHeader = personalInfoSheet.getRow(1);
  personalInfoHeader.eachCell((cell) => {
    Object.assign(cell, getHeaderStyle());
  });
  personalInfoHeader.height = 30;

  const personalInfoDataRow = personalInfoSheet.addRow(personalInfoValues);
  personalInfoDataRow.eachCell((cell, colNumber) => {
    Object.assign(cell, getDataCellStyle(false));

    const fieldNames = fieldNameMap[colNumber - 1];
    if (fieldNames && changedFields && changedFields.length > 0) {
      let changedField: ChangedField | undefined;
      for (const fieldName of fieldNames) {
        changedField = getChangedField(fieldName);
        if (changedField) break;
      }

      if (changedField) {
        let newValue = formatValue(changedField.newValue);
        let oldValue = formatValue(changedField.oldValue || 'Chưa cập nhật');

        if (fieldNames.includes('hometown')) {
          newValue = newValue.replace('Huyện ', '').replace('Tỉnh ', '').replace(', Việt Nam', '');
          oldValue = oldValue.replace('Huyện ', '').replace('Tỉnh ', '').replace(', Việt Nam', '');
        }

        cell.value = {
          richText: [
            { text: newValue, font: { color: { argb: 'FF1890ff' }, bold: true } },
            { text: '\n' },
            { text: oldValue, font: { color: { argb: 'FF8c8c8c' }, strike: true } },
          ],
        };

        personalInfoDataRow.height = Math.max(personalInfoDataRow.height || 25, 50);
      }
    }
  });
  personalInfoDataRow.height = 25;

  // Set độ rộng cột
  personalInfoSheet.getColumn(1).width = 15;
  personalInfoSheet.getColumn(2).width = 25;
  personalInfoSheet.getColumn(3).width = 15;
  personalInfoSheet.getColumn(4).width = 15;
  personalInfoSheet.getColumn(5).width = 15;
  personalInfoSheet.getColumn(6).width = 15;
  personalInfoSheet.getColumn(7).width = 20;
  personalInfoSheet.getColumn(8).width = 20;
  personalInfoSheet.getColumn(9).width = 20;
  personalInfoSheet.getColumn(10).width = 20;
  personalInfoSheet.getColumn(11).width = 15;
  personalInfoSheet.getColumn(12).width = 15;
  personalInfoSheet.getColumn(13).width = 25;
  personalInfoSheet.getColumn(14).width = 20;
  personalInfoSheet.getColumn(15).width = 25;
  personalInfoSheet.getColumn(16).width = 25;
  personalInfoSheet.getColumn(17).width = 20;
  personalInfoSheet.getColumn(18).width = 25;
  personalInfoSheet.getColumn(19).width = 20;
  personalInfoSheet.getColumn(20).width = 20;
  personalInfoSheet.getColumn(21).width = 30;

  // 2. Sheet: Lá số
  const horoscopeSheet = workbook.addWorksheet('Lá số');
  horoscopeSheet.addRow(['Lá số tử vi']);
  const horoscopeHeader = horoscopeSheet.getRow(1);
  horoscopeHeader.eachCell((cell) => {
    Object.assign(cell, getHeaderStyle());
  });
  horoscopeHeader.height = 30;
  horoscopeSheet.getColumn(1).width = 25;

  if (data.horoscope) {
    const horoscopeResult = await fetchImageAsBuffer(data.horoscope);
    if (horoscopeResult) {
      try {
        const imageId = workbook.addImage({
          buffer: horoscopeResult.buffer,
          extension: horoscopeResult.extension,
        });
        horoscopeSheet.addImage(imageId, {
          tl: { col: 0, row: 1 },
          ext: { width: 400, height: 400 },
        });
      } catch (error) {
        console.error('Error adding horoscope image:', error);
      }
    } else {
      horoscopeSheet.addRow([formatValue(data.horoscope || 'Chưa cập nhật')]);
      const dataRow = horoscopeSheet.getRow(2);
      dataRow.eachCell((cell) => {
        Object.assign(cell, getDataCellStyle(false));
      });
      dataRow.height = 25;
    }
  } else {
    horoscopeSheet.addRow(['Chưa cập nhật']);
    const dataRow = horoscopeSheet.getRow(2);
    dataRow.eachCell((cell) => {
      Object.assign(cell, getDataCellStyle(false));
    });
    dataRow.height = 25;
  }

  // 3. Sheet: Giấy tờ tùy thân
  const idDocumentsSheet = workbook.addWorksheet('Giấy tờ tùy thân');
  const idDocumentsHeaders = [
    'Ảnh CCCD/CMT Mặt trước',
    'Ảnh CCCD/CMT Mặt sau',
    'Ảnh hộ chiếu bìa',
    'Ảnh hộ chiếu trang thông tin',
  ];

  idDocumentsSheet.addRow(idDocumentsHeaders);
  const idDocumentsHeader = idDocumentsSheet.getRow(1);
  idDocumentsHeader.eachCell((cell) => {
    Object.assign(cell, getHeaderStyle());
  });
  idDocumentsHeader.height = 30;

  const idDocumentsValues = [
    formatValue(data.idCardFront ? 'Đã có' : 'Chưa cập nhật'),
    formatValue(data.idCardBack ? 'Đã có' : 'Chưa cập nhật'),
    formatValue(data.passportCover ? 'Đã có' : 'Chưa cập nhật'),
    formatValue(data.passportPhoto ? 'Đã có' : 'Chưa cập nhật'),
  ];

  const idDocumentsDataRow = idDocumentsSheet.addRow(idDocumentsValues);
  idDocumentsDataRow.eachCell((cell) => {
    Object.assign(cell, getDataCellStyle(false));
  });
  idDocumentsDataRow.height = 120;

  idDocumentsHeaders.forEach((_, index) => {
    idDocumentsSheet.getColumn(index + 1).width = 30;
  });

  const imageWidth = 200;
  const imageHeight = 120;
  const imageDocuments = [
    { url: data.idCardFront, col: 0 },
    { url: data.idCardBack, col: 1 },
    { url: data.passportCover, col: 2 },
    { url: data.passportPhoto, col: 3 },
  ];

  for (const doc of imageDocuments) {
    if (doc.url) {
      const imageResult = await fetchImageAsBuffer(doc.url);
      if (imageResult) {
        try {
          const imageId = workbook.addImage({
            buffer: imageResult.buffer,
            extension: imageResult.extension,
          });
          idDocumentsSheet.addImage(imageId, {
            tl: { col: doc.col, row: 1 },
            ext: { width: imageWidth, height: imageHeight },
          });
        } catch (error) {
          console.error(`Error adding document image:`, error);
        }
      }
    }
  }

  // 4. Sheet: Thông tin liên hệ
  const contactSheet = workbook.addWorksheet('Thông tin liên hệ');
  const contactHeaders = ['Email', 'Số điện thoại', 'Địa chỉ hiện tại', 'Thường trú'];
  const contactValues = [
    formatValue(data.email),
    formatValue(data.phone),
    formatValue(data.address),
    formatValue(data.permanentAddress),
  ];

  contactSheet.addRow(contactHeaders);
  const contactHeader = contactSheet.getRow(1);
  contactHeader.eachCell((cell) => {
    Object.assign(cell, getHeaderStyle());
  });
  contactHeader.height = 30;

  const contactDataRow = contactSheet.addRow(contactValues);
  contactDataRow.eachCell((cell) => {
    Object.assign(cell, getDataCellStyle(false));
  });
  contactDataRow.height = 25;

  contactSheet.getColumn(1).width = 30;
  contactSheet.getColumn(2).width = 20;
  contactSheet.getColumn(3).width = 40;
  contactSheet.getColumn(4).width = 40;

  // 5. Sheet: Thông tin công việc
  const workInfoSheet = workbook.addWorksheet('Thông tin công việc');
  const workHeaders = ['CTY', 'Phòng ban', 'Chức vụ', 'Vị trí', 'Mức lương', 'Ngày vào làm'];

  workInfoSheet.addRow(workHeaders);
  const workHeader = workInfoSheet.getRow(1);
  workHeader.eachCell((cell) => {
    Object.assign(cell, getHeaderStyle());
  });
  workHeader.height = 30;

  if (data.currentJob) {
    const workRow = workInfoSheet.addRow([
      formatValue(data.currentJob.company),
      formatValue(data.currentJob.department),
      formatValue(formatRole(data.currentJob.position)),
      formatValue(formatRole(data.currentJob.role)),
      formatValue(data.currentJob.salary),
      formatValue(data.currentJob.startDate),
    ]);
    workRow.eachCell((cell) => {
      Object.assign(cell, getDataCellStyle(false));
    });
    workRow.height = 25;
  } else {
    const workRow = workInfoSheet.addRow(['Chưa có thông tin công việc', '', '', '', '', '']);
    workRow.eachCell((cell) => {
      Object.assign(cell, getDataCellStyle(false));
    });
    workRow.height = 25;
  }

  workInfoSheet.getColumn(1).width = 20;
  workInfoSheet.getColumn(2).width = 20;
  workInfoSheet.getColumn(3).width = 20;
  workInfoSheet.getColumn(4).width = 20;
  workInfoSheet.getColumn(5).width = 15;
  workInfoSheet.getColumn(6).width = 15;

  // 6. Sheet: Gia đình và người phụ thuộc
  const familySheet = workbook.addWorksheet('Gia đình và người phụ thuộc');
  familySheet.addRow([
    'Phụ thuộc',
    'Họ và tên',
    'Năm sinh',
    'Số điện thoại',
    'Nghề nghiệp',
    'Nơi làm việc',
    'Địa chỉ',
  ]);
  const familyHeader = familySheet.getRow(1);
  familyHeader.eachCell((cell) => {
    Object.assign(cell, getHeaderStyle());
  });
  familyHeader.height = 30;

  if (data.familyMembers && data.familyMembers.length > 0) {
    data.familyMembers.forEach((member, index) => {
      const row = familySheet.addRow([
        formatValue(member.relationship),
        formatValue(member.name),
        formatValue(member.birthYear),
        formatValue(member.phone),
        formatValue(member.occupation),
        formatValue(member.workplace),
        formatValue(member.address),
      ]);
      row.eachCell((cell) => {
        Object.assign(cell, getDataCellStyle(index % 2 === 0));
      });
      row.height = 25;
    });
  } else {
    const emptyRow = familySheet.addRow(['Chưa có thông tin gia đình', '', '', '', '', '', '']);
    emptyRow.eachCell((cell) => {
      Object.assign(cell, getDataCellStyle(false));
    });
    emptyRow.height = 25;
  }

  familySheet.getColumn(1).width = 25;
  familySheet.getColumn(2).width = 25;
  familySheet.getColumn(3).width = 12;
  familySheet.getColumn(4).width = 18;
  familySheet.getColumn(5).width = 20;
  familySheet.getColumn(6).width = 25;
  familySheet.getColumn(7).width = 40;

  // 7. Sheet: Quá trình học tập
  const educationSheet = workbook.addWorksheet('Quá trình học tập');

  if (data.education) {
    const educationHeaders = ['Trình độ học vấn cao nhất', 'Trình độ phổ thông', 'Chuyên ngành', 'Số năm kinh nghiệm trước đây'];
    const educationValues = [
      formatValue(data.education.highestLevel),
      formatValue(data.education.generalLevel),
      formatValue(data.education.major),
      formatValue(data.education.yearsOfExperience),
    ];

    educationSheet.addRow(educationHeaders);
    const eduHeader = educationSheet.getRow(1);
    eduHeader.eachCell((cell) => {
      Object.assign(cell, getHeaderStyle());
    });
    eduHeader.height = 30;

    const eduDataRow = educationSheet.addRow(educationValues);
    eduDataRow.eachCell((cell) => {
      Object.assign(cell, getDataCellStyle(false));
    });
    eduDataRow.height = 25;

    educationSheet.getColumn(1).width = 30;
    educationSheet.getColumn(2).width = 25;
    educationSheet.getColumn(3).width = 30;
    educationSheet.getColumn(4).width = 30;

    educationSheet.addRow([]);
  }

  if (data.trainingPlaces && data.trainingPlaces.length > 0) {
    educationSheet.addRow(['Nơi đào tạo', 'Chuyên ngành', 'Hình thức đào tạo', 'Trình độ', 'Thời gian']);
    const trainingHeader = educationSheet.getRow(educationSheet.rowCount);
    trainingHeader.eachCell((cell) => {
      Object.assign(cell, getHeaderStyle());
    });
    trainingHeader.height = 30;

    data.trainingPlaces.forEach((place, index) => {
      const row = educationSheet.addRow([
        formatValue(place.place),
        formatValue(place.major),
        formatValue(place.type),
        formatValue(place.degree),
        formatValue(place.time),
      ]);
      row.eachCell((cell) => {
        Object.assign(cell, getDataCellStyle(index % 2 === 0));
      });
      row.height = 25;
    });

    educationSheet.getColumn(1).width = 30;
    educationSheet.getColumn(2).width = 30;
    educationSheet.getColumn(3).width = 25;
    educationSheet.getColumn(4).width = 20;
    educationSheet.getColumn(5).width = 20;
  }

  if (data.workExperience && data.workExperience.length > 0) {
    educationSheet.addRow([]);
    educationSheet.addRow(['Tên CTY', 'Vị trí', 'Phòng ban', 'Mức lương', 'Thời gian']);
    const expHeader = educationSheet.getRow(educationSheet.rowCount);
    expHeader.eachCell((cell) => {
      Object.assign(cell, getHeaderStyle());
    });
    expHeader.height = 30;

    data.workExperience.forEach((exp, index) => {
      const row = educationSheet.addRow([
        formatValue(exp.company),
        formatValue(formatRole(exp.role)),
        formatValue(exp.department),
        formatValue(exp.salary),
        formatValue(exp.time),
      ]);
      row.eachCell((cell) => {
        Object.assign(cell, getDataCellStyle(index % 2 === 0));
      });
      row.height = 25;
    });

    educationSheet.getColumn(1).width = 30;
    educationSheet.getColumn(2).width = 25;
    educationSheet.getColumn(3).width = 25;
    educationSheet.getColumn(4).width = 20;
    educationSheet.getColumn(5).width = 20;
  }

  // 8. Sheet: Thông tin ngân hàng
  const bankSheet = workbook.addWorksheet('Thông tin ngân hàng');
  const bankHeaders = ['Số tài khoản', 'Tên người nhận', 'Ngân hàng', 'Chi nhánh'];
  const bankValues = [
    formatValue(data.bankAccount),
    formatValue(data.bankAccountHolder),
    formatValue(data.bankName),
    formatValue(data.bankBranch),
  ];

  bankSheet.addRow(bankHeaders);
  const bankHeader = bankSheet.getRow(1);
  bankHeader.eachCell((cell) => {
    Object.assign(cell, getHeaderStyle());
  });
  bankHeader.height = 30;

  const bankDataRow = bankSheet.addRow(bankValues);
  bankDataRow.eachCell((cell) => {
    Object.assign(cell, getDataCellStyle(false));
  });
  bankDataRow.height = 25;

  bankSheet.getColumn(1).width = 20;
  bankSheet.getColumn(2).width = 30;
  bankSheet.getColumn(3).width = 25;
  bankSheet.getColumn(4).width = 25;

  // 9. Sheet: Chứng chỉ & Kỹ năng
  const certificatesSheet = workbook.addWorksheet('Chứng chỉ & Kỹ năng');
  certificatesSheet.addRow(['Số hiệu', 'Đơn vị ban hành', 'Loại chứng chỉ', 'Thời gian', 'Ghi chú']);
  const certHeader = certificatesSheet.getRow(1);
  certHeader.eachCell((cell) => {
    Object.assign(cell, getHeaderStyle());
  });
  certHeader.height = 30;

  if (data.certificates && data.certificates.length > 0) {
    data.certificates.forEach((cert, index) => {
      const row = certificatesSheet.addRow([
        formatValue(cert.id),
        formatValue(cert.issuingBody),
        formatValue(cert.type),
        formatValue(cert.time),
        formatValue(cert.notes),
      ]);
      row.eachCell((cell) => {
        Object.assign(cell, getDataCellStyle(index % 2 === 0));
      });
      row.height = 25;
    });
  } else {
    const emptyRow = certificatesSheet.addRow(['Chưa có chứng chỉ nào', '', '', '', '']);
    emptyRow.eachCell((cell) => {
      Object.assign(cell, getDataCellStyle(false));
    });
    emptyRow.height = 25;
  }

  certificatesSheet.getColumn(1).width = 15;
  certificatesSheet.getColumn(2).width = 25;
  certificatesSheet.getColumn(3).width = 20;
  certificatesSheet.getColumn(4).width = 15;
  certificatesSheet.getColumn(5).width = 30;

  // 10. Sheet: Lịch sử Đảng viên
  const partySheet = workbook.addWorksheet('Lịch sử Đảng viên');
  partySheet.addRow(['Số thẻ', 'Hình thức', 'Thời gian', 'Nơi kết nạp', 'Nơi điều chuyển']);
  const partyHeader = partySheet.getRow(1);
  partyHeader.eachCell((cell) => {
    Object.assign(cell, getHeaderStyle());
  });
  partyHeader.height = 30;

  if (data.partyHistory && data.partyHistory.length > 0) {
    data.partyHistory.forEach((history, index) => {
      const row = partySheet.addRow([
        formatValue(history.cardNumber),
        formatValue(history.form),
        formatValue(history.time),
        formatValue(history.admissionPlace),
        formatValue(history.transferPlace),
      ]);
      row.eachCell((cell) => {
        Object.assign(cell, getDataCellStyle(index % 2 === 0));
      });
      row.height = 25;
    });
  } else {
    const emptyRow = partySheet.addRow(['Chưa có lịch sử đảng viên', '', '', '', '']);
    emptyRow.eachCell((cell) => {
      Object.assign(cell, getDataCellStyle(false));
    });
    emptyRow.height = 25;
  }

  partySheet.getColumn(1).width = 15;
  partySheet.getColumn(2).width = 15;
  partySheet.getColumn(3).width = 15;
  partySheet.getColumn(4).width = 25;
  partySheet.getColumn(5).width = 25;

  // 11. Sheet: Kho lưu trữ file cá nhân
  const filesSheet = workbook.addWorksheet('Kho lưu trữ file cá nhân');
  filesSheet.addRow(['Tên file', 'URL']);
  const filesHeader = filesSheet.getRow(1);
  filesHeader.eachCell((cell) => {
    Object.assign(cell, getHeaderStyle());
  });
  filesHeader.height = 30;

  if (data.storedFiles && data.storedFiles.length > 0) {
    data.storedFiles.forEach((file, index) => {
      const row = filesSheet.addRow([formatValue(file.name), formatValue(file.url || 'Không có URL')]);
      row.eachCell((cell) => {
        Object.assign(cell, getDataCellStyle(index % 2 === 0));
      });
      row.height = 25;
    });
  } else {
    const emptyRow = filesSheet.addRow(['Chưa có file nào', '']);
    emptyRow.eachCell((cell) => {
      Object.assign(cell, getDataCellStyle(false));
    });
    emptyRow.height = 25;
  }

  filesSheet.getColumn(1).width = 40;
  filesSheet.getColumn(2).width = 50;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}


